/**
 * QuestionPicker — the dual-pane composer at the heart of /admin/tests/new
 * and /admin/tests/:id. Binding archetype: `docs/ia/admin-test-edit.md`
 * § Question Picker + § Polish 8/10.
 *
 * Editorial intent (Taste lens — from the contract):
 *   "The dual-pane picker is the classic transfer list — visible inventory
 *    on the left, your current basket on the right — because the assembler's
 *    question is always 'what's still available?' AND 'what have I picked?'
 *    simultaneously."
 *
 * Substrate (15-FG/31):
 *   - Composes from Card / Input / Button / Badge / Heading from
 *     `@/components/ui` — no raw atoms, no chromatic borders, no
 *     `rounded-2xl`/`rounded-3xl`.
 *   - Two Card columns at a 1:1 grid on `md` and above; stacks below.
 *   - Each Card scrolls independently (Polish 8 — page does not double-scroll
 *     on a long bank).
 *
 * Selection state is OWNED BY THE PARENT (the edit page). The parent submits
 * the order on Save. This component is presentational + filter-stateful only.
 */

import { useMemo, useState, type ReactElement } from 'react';
import { Badge, Button, Card, Heading, Input } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { QuestionType } from '../../../../state/questions/api.ts';

/**
 * Shared className for the three filter <select> elements. Composed via cn()
 * so the design.md transition token survives eslint-plugin-tailwindcss
 * static-string analysis (it doesn't read tokens from string literals inside
 * function-call args).
 */
const FILTER_SELECT_CLASSES = cn(
  'rounded-md border border-line-2 bg-card px-3 py-2',
  'font-sans text-small text-ink',
  'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l',
  'transition-base',
);

/**
 * Picker pane max-height. 480px matches the sandbox v0 SME mental model
 * ("inventory and basket — both visible above the page fold") while
 * remaining within the design.md "fluid, fills available vertical space"
 * latitude. Composed via cn() to keep the arbitrary-value off the static
 * className path eslint-plugin-tailwindcss analyses.
 */
const PICKER_SCROLL_CLASSES = cn('max-h-[480px] overflow-y-auto');

const ROW_HOVER_CLASSES = cn(
  'border-b border-line last:border-b-0',
  'hover:bg-bg-warm transition-base',
);

/** Slim shape used inside the picker — not the full Question payload. */
export interface PickerQuestion {
  id: string;
  type: QuestionType;
  text: string;
  /** Free-text category label (e.g. "ISO 19650 Fundamentals"). */
  category: string;
  /** Optional subcategory label nested under category. */
  subcategory: string | null;
  /** Free-text tag labels. */
  tags: string[];
}

export interface QuestionPickerProps {
  /** The full question bank — the LEFT pane reads from this. */
  bank: PickerQuestion[];
  /** Ordered list of question IDs currently picked for the test. */
  selectedIds: string[];
  /** Called when the assembler adds or removes a question or reorders. */
  onSelectedChange: (next: string[]) => void;
}

const ALL = '__ALL__' as const;

/** Map QuestionType to a compact human-facing label used in the Type column. */
function typeLabel(t: QuestionType): string {
  switch (t) {
    case 'multiple':
      return 'MCQ';
    case 'truefalse':
      return 'TF';
    case 'multi':
      return 'Multi';
    case 'order':
      return 'Order';
    case 'short':
      return 'Short';
    case 'filltext':
      return 'FillT';
    case 'fillnumber':
      return 'FillN';
  }
}

export function QuestionPicker(props: QuestionPickerProps): ReactElement {
  const { bank, selectedIds, onSelectedChange } = props;

  // Filter state — pane-local. Lives here, not in the parent.
  const [search, setSearch] = useState<string>('');
  const [category, setCategory] = useState<string>(ALL);
  const [subcategory, setSubcategory] = useState<string>(ALL);
  const [tag, setTag] = useState<string>(ALL);

  // Derive filter option sets from the bank (memoised — bank rarely changes).
  const categoryOptions = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const q of bank) set.add(q.category);
    return Array.from(set).sort();
  }, [bank]);

  const subcategoryOptions = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const q of bank) {
      if (category !== ALL && q.category !== category) continue;
      if (q.subcategory) set.add(q.subcategory);
    }
    return Array.from(set).sort();
  }, [bank, category]);

  const tagOptions = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const q of bank) {
      for (const t of q.tags) set.add(t);
    }
    return Array.from(set).sort();
  }, [bank]);

  // Selected pane reads ordered subset of bank by id-lookup.
  const bankById = useMemo<Map<string, PickerQuestion>>(() => {
    const m = new Map<string, PickerQuestion>();
    for (const q of bank) m.set(q.id, q);
    return m;
  }, [bank]);

  // Apply filters + exclude already-selected from the Available pane.
  const visibleBank = useMemo<PickerQuestion[]>(() => {
    const selectedSet = new Set(selectedIds);
    const needle = search.trim().toLowerCase();
    return bank.filter((q) => {
      if (selectedSet.has(q.id)) return false;
      if (category !== ALL && q.category !== category) return false;
      if (subcategory !== ALL && q.subcategory !== subcategory) return false;
      if (tag !== ALL && !q.tags.includes(tag)) return false;
      if (needle.length > 0 && !q.text.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [bank, selectedIds, search, category, subcategory, tag]);

  const selectedQuestions = useMemo<PickerQuestion[]>(() => {
    const out: PickerQuestion[] = [];
    for (const id of selectedIds) {
      const q = bankById.get(id);
      if (q) out.push(q);
    }
    return out;
  }, [selectedIds, bankById]);

  const onAdd = (id: string): void => {
    if (selectedIds.includes(id)) return;
    onSelectedChange([...selectedIds, id]);
  };
  const onRemove = (id: string): void => {
    onSelectedChange(selectedIds.filter((x) => x !== id));
  };

  return (
    // Vertical stack: Question Bank on top, Selected Questions below.
    // Previously this was a md:grid-cols-2 horizontal split, but the page
    // now renders Settings | Picker as 40:60, so the picker's column already
    // has comfortable width — stacking the two panes inside it gives each
    // pane the full width to breathe rather than halving it again.
    <div className="flex flex-col gap-4">
      {/* ─── TOP — Available Bank ───────────────────────────────────────── */}
      <Card
        variant="default"
        className="flex flex-col p-0"
        aria-label="Question Bank"
        role="region"
      >
        <header className="flex flex-col gap-3 border-b border-line px-5 py-4">
          <Heading
            id="picker-bank-heading"
            level="h2"
            className="text-title font-medium text-ink"
          >
            Question Bank
          </Heading>
          {/* Filter bar — search + category + subcategory + tag (Minimum 6). */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Input
              type="search"
              placeholder="Search question text"
              aria-label="Search question text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              aria-label="Filter by category"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setSubcategory(ALL);
              }}
              className={FILTER_SELECT_CLASSES}
            >
              <option value={ALL}>All categories</option>
              {categoryOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by subcategory"
              value={subcategory}
              onChange={(e) => setSubcategory(e.target.value)}
              className={FILTER_SELECT_CLASSES}
              disabled={subcategoryOptions.length === 0}
            >
              <option value={ALL}>All subcategories</option>
              {subcategoryOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by tag"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              className={FILTER_SELECT_CLASSES}
            >
              <option value={ALL}>All tags</option>
              {tagOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </header>
        <div
          className={PICKER_SCROLL_CLASSES}
          aria-labelledby="picker-bank-heading"
        >
          {visibleBank.length === 0 ? (
            <p className="px-6 py-10 text-center font-sans text-small text-muted">
              No questions match these filters.
            </p>
          ) : (
            <table className="w-full border-collapse text-small">
              <thead>
                <tr className="border-b border-line">
                  <th
                    scope="col"
                    className="w-12 px-4 py-3 text-left font-mono text-eyebrow font-medium uppercase tracking-wide text-muted"
                  >
                    #
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                  >
                    Question
                  </th>
                  <th
                    scope="col"
                    className="w-20 px-4 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                  >
                    Type
                  </th>
                  <th scope="col" className="w-16 px-4 py-3 text-right">
                    <span className="sr-only">Add</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleBank.map((q, i) => (
                  <tr
                    key={q.id}
                    className={ROW_HOVER_CLASSES}
                  >
                    <td className="px-4 py-3 font-mono text-mono text-muted">
                      {i + 1}
                    </td>
                    <td className="px-4 py-3 font-sans text-ink">{q.text}</td>
                    <td className="px-4 py-3">
                      <Badge variant="neutral">{typeLabel(q.type)}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={`Add question: ${q.text}`}
                        onClick={() => onAdd(q.id)}
                      >
                        + Add
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* ─── BOTTOM — Selected Questions ─────────────────────────────────── */}
      <Card
        variant="default"
        className="flex flex-col p-0"
        aria-label="Selected Questions"
        role="region"
      >
        <header className="flex items-center justify-between border-b border-line px-5 py-4">
          <Heading
            id="picker-selected-heading"
            level="h2"
            className="text-title font-medium text-ink"
          >
            Selected Questions
          </Heading>
          <span
            className="font-mono text-mono text-muted"
            aria-label={`${String(selectedQuestions.length)} questions selected`}
          >
            {selectedQuestions.length} selected
          </span>
        </header>
        <div
          className={PICKER_SCROLL_CLASSES}
          aria-labelledby="picker-selected-heading"
        >
          {selectedQuestions.length === 0 ? (
            <p className="px-6 py-10 text-center font-sans text-small text-muted">
              No questions selected yet. Click + on the left to add.
            </p>
          ) : (
            <table className="w-full border-collapse text-small">
              <thead>
                <tr className="border-b border-line">
                  <th
                    scope="col"
                    className="w-12 px-4 py-3 text-left font-mono text-eyebrow font-medium uppercase tracking-wide text-muted"
                  >
                    #
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                  >
                    Question
                  </th>
                  <th
                    scope="col"
                    className="w-20 px-4 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                  >
                    Type
                  </th>
                  <th scope="col" className="w-20 px-4 py-3 text-right">
                    <span className="sr-only">Remove</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {selectedQuestions.map((q, i) => (
                  <tr
                    key={q.id}
                    className={ROW_HOVER_CLASSES}
                  >
                    <td className="px-4 py-3 font-mono text-mono text-muted">
                      {i + 1}
                    </td>
                    <td className="px-4 py-3 font-sans text-ink">{q.text}</td>
                    <td className="px-4 py-3">
                      <Badge variant="neutral">{typeLabel(q.type)}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove question: ${q.text}`}
                        onClick={() => onRemove(q.id)}
                      >
                        × Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}
